import React, { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { cn } from '@/utils/cn';
import {
  AgentFormValues,
  PromptCategory,
  PROMPT_CATEGORIES,
  PROMPT_VARIABLES,
  TENANT_ROLES,
  inputCls,
} from './agentShared';

// ── RolePicker (inline panel) ──────────────────────────────────────────────────

const RolePicker: React.FC<{
  selected: string[];
  onChange: (roles: string[]) => void;
  onDone: () => void;
}> = ({ selected, onChange, onDone }) => {
  const toggle = (role: string) => {
    onChange(selected.includes(role) ? selected.filter((r) => r !== role) : [...selected, role]);
  };

  return (
    <div className="border-b border-[#EDEDED] dark:border-white/10 bg-ui-card px-4 py-3">
      <p className="mb-2 text-xs text-ui-text3">
        Block is injected only for the selected roles. Leave empty to inject for everyone.
      </p>
      <div className="flex flex-wrap gap-2">
        {TENANT_ROLES.map((role) => {
          const checked = selected.includes(role.value);
          return (
            <label
              key={role.value}
              className={cn(
                'inline-flex cursor-pointer select-none items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs transition',
                checked
                  ? 'border-brand-dark/30 bg-brand-dark/5 font-medium text-[#272727]'
                  : 'border-[#EDEDED] dark:border-white/10 bg-ui-card text-ui-text2 hover:bg-ui-hover',
              )}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(role.value)}
                className="h-3 w-3 accent-brand-dark"
              />
              {role.label}
            </label>
          );
        })}
      </div>
      <div className="mt-2.5 flex items-center justify-between">
        {selected.length > 0 ? (
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-xs text-[#CF4148] hover:underline"
          >
            Clear — always inject
          </button>
        ) : (
          <span className="text-xs text-ui-text3">No restriction — always injected</span>
        )}
        <button
          type="button"
          onClick={onDone}
          className="text-xs font-medium text-brand-dark hover:underline"
        >
          Done
        </button>
      </div>
    </div>
  );
};

// ── PromptBlock ────────────────────────────────────────────────────────────────

interface PromptBlockProps {
  index: number;
  total: number;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}

const PromptBlock: React.FC<PromptBlockProps> = ({ index, total, onMove, onRemove }) => {
  const { register, watch, setValue } = useFormContext<AgentFormValues>();
  const [showCatPicker, setShowCatPicker] = useState(false);
  const [showRolePicker, setShowRolePicker] = useState(false);
  const [customTag, setCustomTag] = useState('');

  const category = watch(`system_prompts.${index}.category`) as PromptCategory | undefined;
  const conditionRoles = (watch(`system_prompts.${index}.condition_roles`) as string[] | undefined) ?? [];
  const catCfg = category ? PROMPT_CATEGORIES[category] : null;

  // ── variable insertion ─────────────────────────────────────────────────────

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const { ref: rhfRef, onChange, onBlur, name } = register(`system_prompts.${index}.prompt`);

  const combineRef = (el: HTMLTextAreaElement | null) => {
    textareaRef.current = el;
    if (typeof rhfRef === 'function') {
      rhfRef(el);
    } else if (rhfRef) {
      (rhfRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
    }
  };

  const insertVariable = (token: string) => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? start;
    const snippet = `{{ ${token} }}`;
    const newValue = el.value.slice(0, start) + snippet + el.value.slice(end);
    setValue(`system_prompts.${index}.prompt`, newValue, { shouldDirty: true });
    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      const pos = start + snippet.length;
      textareaRef.current.setSelectionRange(pos, pos);
      textareaRef.current.focus();
    });
  };

  // ── setters ────────────────────────────────────────────────────────────────

  const setCategory = (cat: PromptCategory | undefined) => {
    setValue(`system_prompts.${index}.category`, cat as never, { shouldDirty: true });
    setShowCatPicker(false);
  };

  const setRoles = (roles: string[]) => {
    setValue(`system_prompts.${index}.condition_roles`, roles as never, { shouldDirty: true });
  };

  const toggleCatPicker = () => { setShowCatPicker((p) => !p); setShowRolePicker(false); };
  const toggleRolePicker = () => { setShowRolePicker((p) => !p); setShowCatPicker(false); };

  // ── role badge label ───────────────────────────────────────────────────────

  const roleBadgeLabel =
    conditionRoles.length > 0
      ? conditionRoles
          .map((r) => TENANT_ROLES.find((t) => t.value === r)?.label ?? r)
          .join(', ')
      : null;

  return (
    <div className="overflow-hidden rounded-xl border border-[#EDEDED] dark:border-white/10">

      {/* ── header ── */}
      <div className="flex items-center gap-2 border-b border-[#EDEDED] dark:border-white/10 bg-ui-raised px-3 py-2.5">
        {/* move */}
        <div className="flex shrink-0 flex-col gap-0.5">
          <button type="button" onClick={() => onMove(-1)} disabled={index === 0}
            className="text-[10px] leading-none text-ui-text3 hover:text-ui-text2 disabled:opacity-25">▲</button>
          <button type="button" onClick={() => onMove(1)} disabled={index === total - 1}
            className="text-[10px] leading-none text-ui-text3 hover:text-ui-text2 disabled:opacity-25">▼</button>
        </div>

        {/* category chip */}
        <button
          type="button"
          onClick={toggleCatPicker}
          className={cn(
            'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium transition',
            catCfg
              ? catCfg.chip
              : category
              ? 'border-gray-300 bg-ui-subtle text-ui-text2'
              : 'border-[#EDEDED] dark:border-white/10 bg-ui-card text-ui-text3 hover:border-gray-300',
          )}
        >
          {catCfg && <span className={cn('h-1.5 w-1.5 rounded-full', catCfg.dot)} />}
          {catCfg ? catCfg.label : category ? category : '+ tag'}
          <span className="text-[10px] opacity-50">▾</span>
        </button>

        {/* role condition badge */}
        {roleBadgeLabel ? (
          <button
            type="button"
            onClick={toggleRolePicker}
            className="inline-flex shrink-0 items-center gap-1 rounded-full border border-brand-light bg-brand-light/40 px-2 py-0.5 text-xs font-medium text-brand-dark transition hover:bg-brand-light/60"
          >
            <span className="text-[10px]">◎</span>
            {roleBadgeLabel}
          </button>
        ) : (
          <button
            type="button"
            onClick={toggleRolePicker}
            className="shrink-0 text-xs text-ui-text3 transition hover:text-ui-text2"
          >
            + roles
          </button>
        )}

        {/* title — borderless fill */}
        <input
          className="min-w-0 flex-1 bg-transparent text-sm text-ui-text placeholder:text-ui-text3 focus:outline-none"
          placeholder="Title (optional)"
          {...register(`system_prompts.${index}.title`)}
        />

        {/* remove */}
        <button type="button" onClick={onRemove}
          className="shrink-0 text-xs text-[#CF4148] hover:underline">
          Remove
        </button>
      </div>

      {/* ── category picker ── */}
      {showCatPicker && (
        <div className="flex flex-wrap items-center gap-1.5 border-b border-[#EDEDED] dark:border-white/10 bg-ui-card px-3 py-2.5">
          {(Object.entries(PROMPT_CATEGORIES) as [PromptCategory, typeof PROMPT_CATEGORIES[PromptCategory]][]).map(
            ([key, cfg]) => (
              <button
                key={key}
                type="button"
                onClick={() => setCategory(category === key ? undefined : key)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition',
                  category === key ? cfg.chip : 'border-[#EDEDED] dark:border-white/10 bg-ui-card text-ui-text2 hover:bg-ui-hover',
                )}
              >
                <span className={cn('h-1.5 w-1.5 rounded-full', cfg.dot)} />
                {cfg.label}
              </button>
            ),
          )}
          {category && (
            <button type="button" onClick={() => setCategory(undefined)}
              className="ml-1 text-xs text-ui-text3 hover:text-ui-text2">
              Clear
            </button>
          )}

          {/* Custom tag */}
          <div className="ml-auto flex items-center gap-1">
            <input
              value={customTag}
              onChange={(e) => setCustomTag(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const val = customTag.trim().toLowerCase().replace(/\s+/g, '_');
                  if (val) { setCategory(val as PromptCategory); setCustomTag(''); }
                }
              }}
              placeholder="custom tag…"
              className="rounded border border-[#EDEDED] dark:border-white/10 bg-ui-input px-2 py-0.5 text-xs text-ui-text2 placeholder:text-ui-text3 focus:border-brand-light focus:outline-none w-28"
            />
            <button
              type="button"
              onClick={() => {
                const val = customTag.trim().toLowerCase().replace(/\s+/g, '_');
                if (val) { setCategory(val as PromptCategory); setCustomTag(''); }
              }}
              className="text-xs text-brand-dark hover:underline"
            >
              Set
            </button>
          </div>
        </div>
      )}

      {/* ── role picker ── */}
      {showRolePicker && (
        <RolePicker
          selected={conditionRoles}
          onChange={setRoles}
          onDone={() => setShowRolePicker(false)}
        />
      )}

      {/* ── prompt body ── */}
      <div className="bg-ui-card p-3">
        <textarea
          ref={combineRef}
          name={name}
          onChange={onChange}
          onBlur={onBlur}
          rows={4}
          placeholder="Prompt content"
          className={cn(inputCls, 'font-mono text-sm leading-relaxed')}
        />

        {/* variable chips — grouped by User / Tenant */}
        <div className="mt-2.5 space-y-1.5">
          {(['User', 'Tenant'] as const).map((group) => {
            const vars = PROMPT_VARIABLES.filter((v) => v.group === group);
            return (
              <div key={group} className="flex flex-wrap items-center gap-1.5">
                <span className="w-12 shrink-0 text-[11px] font-medium text-ui-text3">{group}</span>
                {vars.map((v) => (
                  <button
                    key={v.token}
                    type="button"
                    onClick={() => insertVariable(v.token)}
                    title={`{{ ${v.token} }}`}
                    className="rounded-md border border-[#EDEDED] dark:border-white/10 bg-ui-raised px-2 py-0.5 text-[11px] text-ui-text2 transition hover:border-brand-light hover:bg-brand-light/30 hover:text-brand-dark"
                  >
                    {v.short}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PromptBlock;
